import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, Tag } from 'lucide-react';
import { Reveal } from '../hooks/useReveal';
import TrialCTA from '../components/TrialCTA';
import { ARTICLES, getArticle, readingTime, formatDate } from '../data/blog';

const SITE = 'https://hybrid-training-club.com';

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  // JSON-LD BlogPosting — inject on mount, remove on unmount / slug change.
  useEffect(() => {
    if (!article) return undefined;
    const canonical = `${SITE}/blog/${article.slug}`;
    const imageAbs = `${SITE}${article.featured}`;
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: imageAbs,
      datePublished: article.date,
      dateModified: article.date,
      keywords: article.keyword,
      author: {
        '@type': 'Organization',
        name: 'Hybrid Training Club',
        url: SITE,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Hybrid Training Club',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE}/logo-h-128.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-blog-jsonld', 'true');
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const minutes = readingTime(article.content);
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      {/* Hero with featured image background (like ClubPage) */}
      <section className="relative pt-32 pb-14 md:pt-56 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.featured}
            alt={article.title}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-2 font-cond text-xs tracking-widest text-[var(--hx-muted)] mb-5">
            <Link to="/" className="hover:text-accent transition-colors">Accueil</Link>
            <span className="text-[#3a3a3a]">›</span>
            <Link to="/blog" className="hover:text-accent transition-colors">Blog</Link>
            <span className="text-[#3a3a3a]">›</span>
            <span className="text-[var(--hx-text)] normal-case tracking-normal">{article.title}</span>
          </nav>

          <h1 className="font-display text-4xl md:text-6xl leading-[0.95] max-w-3xl">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-5 mt-6 font-cond text-sm tracking-wider text-[var(--hx-muted)]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={15} className="text-accent" />
              {formatDate(article.date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={15} className="text-accent" />
              {minutes} min de lecture
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Tag size={15} className="text-accent" />
              {article.keyword}
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <article
            className="hx-prose"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </section>

      {/* À lire aussi */}
      <section className="py-14 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-10">
            <p className="hx-eyebrow mb-4">À lire aussi</p>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95]">
              Continue ta lecture
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={i * 100}>
                <Link
                  to={`/blog/${a.slug}`}
                  className="hx-card group rounded-md overflow-hidden h-full flex flex-col"
                >
                  <div className="h-40 overflow-hidden relative">
                    <img
                      src={a.featured}
                      alt={a.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#131313] to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-lg leading-[1.05] mb-2 group-hover:text-accent transition-colors">
                      {a.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-accent font-cond text-sm tracking-wider mt-auto pt-3">
                      Lire{' '}
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TrialCTA />
    </>
  );
}
